import { api, apiAntigo } from "services/axios/api";
import { getErrorsByApi } from "shared/helpers/getErrorsByApi";
import {
  IListarProdutosN1Response,
  IFiltrosListagemN1,
  IOrdenacaoListagemN1,
  IPaginacaoListagemN1,
  TipoMaterialN1,
  ITiposPermitidosResponse,
  IAcaoEmLoteRequest,
  IAcaoEmLoteResponse,
  IImportarProdutosRequest,
  IResultadoImportacao,
  IHistoricoImportacao,
  IExportarProdutosRequest,
} from "../types/materiaisN1.types";

// Mapeamento de TipoMaterialN1 para tipo usado na API antiga
const MAP_TIPO_MATERIAL_API: Record<TipoMaterialN1, string> = {
  perfis: 'Perfil',
  componentes: 'Componente',
  kits: 'Kit',
  superficies: 'Superficie',
  vidros: 'Vidro',
  acabamentos: 'Acabamento',
};

/**
 * Lista produtos N1 com filtros, ordenação e paginação
 */
export const listarProdutosN1 = async (
  tipoMaterial: TipoMaterialN1,
  paginacao: IPaginacaoListagemN1,
  filtros?: IFiltrosListagemN1,
  ordenacao?: IOrdenacaoListagemN1,
  modoEdicao: boolean = false
): Promise<IListarProdutosN1Response | null> => {
  try {
    // Usar novo endpoint do backend_novo
    // Converter page de base 0 (frontend) para base 1 (backend)
    // FASE 05 — 4ª validação/D2 2026-07-30: arrays vão como chave repetida SEM colchetes
    // (situacao=a&situacao=b). O formato padrão do axios ("situacao[]=") não é reconhecido
    // pelo model binder do ASP.NET Core e os filtros chegavam sempre nulos no servidor.
    const params = new URLSearchParams();
    params.append("tipoMaterial", tipoMaterial);
    params.append("modoEdicao", String(modoEdicao));
    params.append("page", String(paginacao.page + 1)); // Backend usa base 1
    params.append("limit", String(paginacao.limit));

    // Adicionar filtros
    if (filtros?.codigo) params.append("codigo", filtros.codigo);
    if (filtros?.descricao) params.append("descricao", filtros.descricao);
    if (filtros?.referencia) params.append("referencia", filtros.referencia);
    if (filtros?.linha) params.append("linha", filtros.linha);
    filtros?.status?.forEach((s) => params.append("status", s));
    filtros?.situacao?.forEach((s) => params.append("situacao", s));

    // Adicionar ordenação
    if (ordenacao?.campo) {
      params.append("campoOrdenacao", ordenacao.campo);
      params.append("direcaoOrdenacao", ordenacao.direcao || "asc");
    }

    console.log("[listarProdutosN1] Parâmetros da requisição:", params.toString());
    
    const response = await api.get<{
      items: any[];
      totalRecords: number;
      page: number;
      totalPages: number;
    }>("/ProdutoN1/Listar", { params });
    
    console.log("[listarProdutosN1] Resposta completa da API:", response);
    console.log("[listarProdutosN1] Response.data:", response.data);
    const { data } = response;

    // A API retorna o objeto diretamente (não está em data.data)
    if (data) {
      // Verificar se é um PagedResponse
      if (data.items !== undefined) {
        return {
          items: (data.items || []).map((item: any) => ({
            // Garantir que os campos sejam lidos corretamente (suporta tanto camelCase quanto PascalCase)
            id: item.id ?? item.Id ?? 0,
            codigo: item.codigo ?? item.Codigo ?? '',
            descricao: item.descricao ?? item.Descricao ?? '',
            referencia: item.referencia ?? item.Referencia ?? '',
            linha: item.linha ?? item.Linha ?? '',
            status: (item.status ?? item.Status ?? 'inativo') as 'ativo' | 'descontinuado' | 'inativo',
            situacao: (item.situacao ?? item.Situacao ?? 'em_edicao') as 'em_edicao' | 'em_homologacao' | 'homologado' | 'disponivel_mercado' | 'indisponivel',
            urlImagem: item.urlImagem ?? item.UrlImagem ?? null,
            temImagem: item.temImagem ?? item.TemImagem ?? false,
            origem: (item.origem ?? item.Origem ?? 'Empresa') as 'Empresa' | 'BaseMof',
            editavel: item.editavel ?? item.Editavel ?? true,
          })),
          total: data.totalRecords || 0,
          page: (data.page || paginacao.page + 1) - 1, // Converter de base 1 para base 0
          limit: paginacao.limit,
        };
      }
    }

    // Se não retornou dados válidos, retorna lista vazia
    console.warn("[listarProdutosN1] Resposta não contém items, retornando lista vazia");
    return {
      items: [],
      total: 0,
      page: paginacao.page,
      limit: paginacao.limit,
    };
  } catch (error: any) {
    console.error("[listarProdutosN1] Erro completo:", error);
    console.error("[listarProdutosN1] Erro response:", error?.response);
    console.error("[listarProdutosN1] Erro data:", error?.response?.data);
    console.error("[listarProdutosN1] Erro status:", error?.response?.status);
    console.error("[listarProdutosN1] Erro message:", error?.message);
    
    // Se for erro 400 (BadRequest), tenta extrair mensagem mas não quebra a aplicação
    if (error?.response?.status === 400) {
      const errorData = error?.response?.data;
      const errorMessage = errorData?.message || 
                          (Array.isArray(errorData?.Mensagens) ? errorData.Mensagens.join(", ") : errorData?.Mensagens) ||
                          "Erro ao listar produtos";
      
      // Se for erro de referências ou empresa, retorna lista vazia
      if (errorMessage.includes("referência") || 
          errorMessage.includes("Empresa") || 
          errorMessage.includes("material inválido")) {
        console.warn("[listarProdutosN1] Erro tratado, retornando lista vazia:", errorMessage);
        return {
          items: [],
          total: 0,
          page: paginacao.page,
          limit: paginacao.limit,
        };
      }
      
      // Para outros erros 400, mostra notificação mas retorna lista vazia
      console.warn("[listarProdutosN1] Erro 400, retornando lista vazia:", errorMessage);
      return {
        items: [],
        total: 0,
        page: paginacao.page,
        limit: paginacao.limit,
      };
    }
    
    // Para outros erros (500, etc), tenta mostrar erro mas não quebra
    try {
      getErrorsByApi(error, "Erro ao listar produtos");
    } catch (e) {
      console.error("[listarProdutosN1] Erro ao processar erro da API:", e);
    }
    
    // Sempre retorna lista vazia ao invés de null para não quebrar a aplicação
    return {
      items: [],
      total: 0,
      page: paginacao.page,
      limit: paginacao.limit,
    };
  }
};

/**
 * Mapeia status da API antiga para novo formato
 */
function mapearStatus(status: string): 'ativo' | 'descontinuado' | 'inativo' {
  const statusLower = status?.toLowerCase() || '';
  if (statusLower.includes('ativo')) return 'ativo';
  if (statusLower.includes('descontinuado')) return 'descontinuado';
  return 'inativo';
}

/**
 * Mapeia situação da API antiga para novo formato
 */
function mapearSituacao(situacao: string): 'em_edicao' | 'em_homologacao' | 'homologado' | 'disponivel_mercado' {
  const situacaoLower = situacao?.toLowerCase() || '';
  if (situacaoLower.includes('edição') || situacaoLower.includes('edicao')) return 'em_edicao';
  if (situacaoLower.includes('homologação') || situacaoLower.includes('homologacao')) return 'em_homologacao';
  if (situacaoLower.includes('homologado')) return 'homologado';
  if (situacaoLower.includes('disponível') || situacaoLower.includes('disponivel')) return 'disponivel_mercado';
  return 'em_edicao';
}

/**
 * Obtém tipos de materiais permitidos para a empresa
 */
export const obterTiposPermitidos = async (idEmpresa: number): Promise<ITiposPermitidosResponse | null> => {
  try {
    const { data } = await api.get<{
      tipos: Array<{
        idTipoMaterial: number;
        tipoMaterial: string;
        podeVisualizar: boolean;
        podeEditar: boolean;
        podeIncluir: boolean;
        podeImportar: boolean;
      }>;
      apenasConsulta: boolean;
      permissoes: {
        podeCadastrar: boolean;
        podeImportar: boolean;
        podeExportar: boolean;
      };
    }>("/ProdutoN1/ObterTiposPermitidos");

    // Log temporário para debug
    console.log("[obterTiposPermitidos] Resposta da API:", data);
    console.log("[obterTiposPermitidos] Tipos recebidos:", data?.tipos?.length || 0);

    if (data && data.tipos) {
      return {
        tipos: data.tipos.map(t => ({
          idTipoMaterial: t.idTipoMaterial,
          tipoMaterial: t.tipoMaterial as TipoMaterialN1,
          podeVisualizar: t.podeVisualizar,
          podeEditar: t.podeEditar,
          podeIncluir: t.podeIncluir,
          podeImportar: t.podeImportar,
        })),
        apenasConsulta: data.apenasConsulta ?? false,
        permissoes: data.permissoes || {
          podeCadastrar: false,
          podeImportar: false,
          podeExportar: true,
        },
      };
    }

    return {
      tipos: [],
      apenasConsulta: false,
      permissoes: {
        podeCadastrar: false,
        podeImportar: false,
        podeExportar: true,
      },
    };
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao obter tipos permitidos");
    return null;
  }
};

/**
 * FASE 05 — Ajustes 25/08 (item 7): um acionamento = UMA chamada, com todos os tipos.
 *
 * Antes a tela chamava o endpoint uma vez por tipo e o servidor mandava um e-mail por
 * chamada — selecionar produtos de 4 tipos rendia 4 e-mails. Agora vai uma lista só e sai
 * uma notificação com todos os produtos afetados, como a notificação in-app já fazia.
 *
 * De quebra, a geração dos pacotes do CEM passa a ser disparada UMA vez por acionamento
 * (antes: uma vez por tipo, cada uma regerando todas as referências da empresa).
 */
export interface ITipoSelecionadoN1 {
  tipoMaterial: TipoMaterialN1;
  idsProdutos: number[];
}

const executarAcaoEmLote = async (
  rota: "Homologar" | "ConcluirHomologacao" | "LiberarCem",
  idEmpresa: number,
  itens: ITipoSelecionadoN1[],
  extra?: Record<string, unknown>
): Promise<IAcaoEmLoteResponse | null> => {
  try {
    // A API responde { sucesso, total } direto, sem envelope (A3 da 6ª validação de 03/08).
    const { data } = await api.post<{ sucesso: boolean; total: number }>(
      `/ProdutoN1/${rota}`,
      {
        idEmpresa,
        itens,
        // Compatibilidade: a DLL e o bundle não sobem no mesmo instante. Enquanto a API
        // antiga estiver no ar, ela ignora `itens` e usa estes dois campos — o que preserva
        // o comportamento anterior (um tipo por chamada) em vez de não fazer nada.
        tipoMaterial: itens[0]?.tipoMaterial,
        idsProdutos: itens[0]?.idsProdutos ?? [],
        ...extra,
      }
    );

    return { sucesso: data.sucesso, total: data.total };
  } catch (error: any) {
    getErrorsByApi(error, `Erro ao executar a ação (${rota})`);
    return null;
  }
};

export const homologarProdutosEmLote = (idEmpresa: number, itens: ITipoSelecionadoN1[]) =>
  executarAcaoEmLote("Homologar", idEmpresa, itens);

export const concluirHomologacaoEmLote = (idEmpresa: number, itens: ITipoSelecionadoN1[]) =>
  executarAcaoEmLote("ConcluirHomologacao", idEmpresa, itens);

export const liberarCemEmLote = (
  idEmpresa: number,
  itens: ITipoSelecionadoN1[],
  cem: "delphi" | "mobi"
) => executarAcaoEmLote("LiberarCem", idEmpresa, itens, { cem });

/**
 * Disponibiliza itens selecionados para CEM (Delphi/Mobi)
 * Observação: no escopo da tela, a ação altera a situação para "disponivel_mercado".
 */
export const liberarCemSelecionados = async (
  tipoMaterial: TipoMaterialN1,
  idsProdutos: number[],
  cem: "delphi" | "mobi"
): Promise<{ sucesso: boolean; total: number } | null> => {
  try {
    const { data } = await api.post<{ sucesso: boolean; total: number }>(
      "/ProdutoN1/LiberarCem",
      {
        tipoMaterial,
        idsProdutos,
        cem,
      }
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao liberar CEM");
    return null;
  }
};

/**
 * Mapeia ID do tipo de material para TipoMaterialN1
 */
function mapearIdTipoMaterialParaTipo(idTipoMaterial: number): TipoMaterialN1 | null {
  const map: Record<number, TipoMaterialN1> = {
    1: 'perfis',
    2: 'componentes',
    3: 'vidros',
    4: 'superficies',
    5: 'acabamentos',
    6: 'kits',
  };
  return map[idTipoMaterial] || null;
}

/**
 * Obtém descrição do tipo de material pelo ID
 */
function obterDescricaoTipoMaterial(idTipoMaterial: number): string {
  const map: Record<number, string> = {
    1: 'Perfis',
    2: 'Componentes',
    3: 'Vidros',
    4: 'Superfícies',
    5: 'Acabamentos',
    6: 'Kits',
  };
  return map[idTipoMaterial] || 'Desconhecido';
}

/**
 * Homologa produtos selecionados
 */
export const homologarProdutos = async (
  request: IAcaoEmLoteRequest,
  tipoMaterial: TipoMaterialN1
): Promise<IAcaoEmLoteResponse | null> => {
  try {
    // FASE 05 — 6ª validação/PO 2026-08-03 (A3): a API responde { sucesso, total } (sem
    // envelope { success, data }). O parsing antigo lia data.success (inexistente) e sempre
    // devolvia sucesso=false → o front mostrava "nenhum processado" mesmo com o backend
    // gravando (Homologar/Concluir retornam 200). Lê-se o corpo direto, como no LiberarCem.
    const { data } = await api.post<{
      sucesso: boolean;
      total: number;
    }>("/ProdutoN1/Homologar", {
      idEmpresa: request.idEmpresa,
      tipoMaterial: tipoMaterial,
      idsProdutos: request.idsProdutos,
    });

    return {
      sucesso: data.sucesso,
      total: data.total,
    };
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao homologar produtos");
    return null;
  }
};

/**
 * Conclui homologação de produtos selecionados
 */
export const concluirHomologacao = async (
  request: IAcaoEmLoteRequest,
  tipoMaterial: TipoMaterialN1
): Promise<IAcaoEmLoteResponse | null> => {
  try {
    // FASE 05 — 6ª validação/PO 2026-08-03 (A3): a API responde { sucesso, total } (sem
    // envelope { success, data }). Provado em produção: POST /ConcluirHomologacao = 200 e o
    // PO via "nenhum processado" só por causa deste parsing. Lê-se o corpo direto.
    const { data } = await api.post<{
      sucesso: boolean;
      total: number;
    }>("/ProdutoN1/ConcluirHomologacao", {
      idEmpresa: request.idEmpresa,
      tipoMaterial: tipoMaterial,
      idsProdutos: request.idsProdutos,
    });

    return {
      sucesso: data.sucesso,
      total: data.total,
    };
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao concluir homologação");
    return null;
  }
};

/**
 * Importa produtos de arquivo
 */
export const importarProdutos = async (request: IImportarProdutosRequest): Promise<IResultadoImportacao | null> => {
  try {
    const formData = new FormData();
    formData.append('arquivo', request.arquivo);
    formData.append('tipoMaterial', request.tipoMaterial);

    const { data } = await api.post("/ProdutoN1/Importar", formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return {
      sucesso: data.sucesso || false,
      linhasAceitas: data.linhasAceitas || 0,
      linhasRecusadas: data.linhasRecusadas || 0,
      erros: data.erros,
      idImportacao: data.idImportacao,
    };
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao importar produtos");
    return null;
  }
};

/**
 * Baixa modelo de planilha para importação
 */
export const baixarModeloPlanilha = async (tipoMaterial: TipoMaterialN1): Promise<void> => {
  try {
    const response = await api.get(`/ProdutoN1/BaixarModeloPlanilha?tipoMaterial=${tipoMaterial}`, {
      responseType: 'blob',
    });

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `modelo_importacao_${tipoMaterial}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao baixar modelo de planilha");
    // FASE 05 — 3ª validação/PO 2026-07-28 (Frente 11): re-lança para o chamador não exibir
    // sucesso quando o download falhou (antes o botão "não fazia nada" com toast de sucesso).
    throw error;
  }
};

/**
 * Exporta produtos para planilha
 */
export const exportarProdutos = async (request: IExportarProdutosRequest): Promise<void> => {
  try {
    // Construir parâmetros de query
    // FASE 05 — 4ª validação/D2 2026-07-30: mesmo formato da listagem — arrays como chave
    // repetida sem colchetes, senão o servidor ignora os filtros (export ≠ tela).
    const params = new URLSearchParams();
    params.append("tipoMaterial", request.tipoMaterial);
    params.append("modoEdicao", String(request.modoEdicao ?? false));

    if (request.filtros?.codigo) params.append("codigo", request.filtros.codigo);
    if (request.filtros?.descricao) params.append("descricao", request.filtros.descricao);
    if (request.filtros?.referencia) params.append("referencia", request.filtros.referencia);
    if (request.filtros?.linha) params.append("linha", request.filtros.linha);
    request.filtros?.status?.forEach((s) => params.append("status", s));
    request.filtros?.situacao?.forEach((s) => params.append("situacao", s));

    const response = await api.get("/ProdutoN1/Exportar", {
      params,
      responseType: 'blob',
    });

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const dataAtual = new Date().toISOString().split('T')[0];
    link.setAttribute('download', `exportacao_produtos_${request.tipoMaterial}_${dataAtual}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao exportar produtos");
  }
};

/**
 * Obtém histórico de importações
 */
export const obterHistoricoImportacoes = async (
  _idEmpresa: number,
  tipoMaterial: TipoMaterialN1
): Promise<IHistoricoImportacao[]> => {
  try {
    const { data } = await api.get<IHistoricoImportacao[]>(`/ProdutoN1/HistoricoImportacao?tipoMaterial=${tipoMaterial}`);
    return data || [];
  } catch (error: any) {
    console.warn("[obterHistoricoImportacoes] Erro ao buscar histórico:", error);
    // Retorna array vazio em caso de erro para não quebrar a aplicação
    return [];
  }
};

/**
 * Remove última importação
 */
export const removerUltimaImportacao = async (idImportacao: number): Promise<boolean> => {
  try {
    await api.delete(`/ProdutoN1/RemoverUltimaImportacao/${idImportacao}`);
    return true;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao remover importação");
    return false;
  }
};

/**
 * Andamento da geração dos pacotes de N1 para o CEM Desktop (decisão P9 de 17/08/2026).
 * O estado vem do servidor — sobrevive a um F5 no meio do processo.
 */
export interface IAndamentoGeracaoPacotes {
  emAndamento: boolean;
  situacao?: number; // 0 = em andamento, 1 = concluída, 2 = falhou
  modo?: number; // 1 = homologação, 2 = mercado
  dataInicio?: string;
  dataFim?: string;
  totalPacotes?: number;
  totalFalhas?: number;
  mensagem?: string;
}

// FASE 05 Ajustes — PO 2026-08-24 (item 6 / P1): contagem multi-tipo de produtos elegíveis
// por situação de origem — decide a VISIBILIDADE dos botões de fluxo (BarraAcoesStatus).
export interface IContagemSituacoes {
  emEdicao: number | null;
  emHomologacao: number | null;
  homologado: number | null;
}

// FASE 05 — Ajustes 25/08 (F05A-P1): a contagem é por PÁGINA — a de Acabamentos não pode
// contar perfis elegíveis, senão o botão apareceria lá sem ter o que processar.
export const obterContagemSituacoes = async (tipos?: TipoMaterialN1[]): Promise<IContagemSituacoes | null> => {
  try {
    const query = tipos?.length ? "?tiposMaterial=" + tipos.join(",") : "";
    const { data } = await api.get<IContagemSituacoes>("/ProdutoN1/ObterContagemSituacoes" + query);
    return data;
  } catch (error: any) {
    // Silencioso: falha na contagem não pode esconder botão legítimo — o componente trata
    // null como "manter visível" (comportamento anterior).
    console.error("[API ERROR] contagem de situações dos produtos N1", error?.response?.status);
    return null;
  }
};

// Plano 14/10/2026 (Frente 4): estado da trava planilha × edição manual do tipo na empresa
// (exclusão mútua, P2 de 13/10). A recusa definitiva é do back; a tela só se antecipa.
export interface ITravaPlanilhaN1 {
  planilhaPendente: boolean;
  edicaoManualPendente: boolean;
  mensagemPlanilhaPendente: string;
  mensagemEdicaoManualPendente: string;
}

export const obterTravaPlanilha = async (tipoMaterial: TipoMaterialN1): Promise<ITravaPlanilhaN1 | null> => {
  try {
    const { data } = await api.get<ITravaPlanilhaN1>(
      "/ProdutoN1/TravaPlanilha?tipoMaterial=" + encodeURIComponent(tipoMaterial)
    );
    return data;
  } catch (error: any) {
    // Silencioso: sem o estado a tela segue como antes e o back recusa com a mensagem da trava.
    console.error("[API ERROR] trava planilha × edição manual", error?.response?.status);
    return null;
  }
};

export const obterAndamentoGeracaoPacotes = async (): Promise<IAndamentoGeracaoPacotes | null> => {
  try {
    const { data } = await api.get<IAndamentoGeracaoPacotes>(
      "/ProdutoN1/ObterAndamentoGeracaoPacotes"
    );
    return data;
  } catch (error: any) {
    // Silencioso de propósito: a consulta de andamento roda em laço e um erro aqui não
    // pode encher a tela de alerta — a ação principal (troca de situação) já concluiu.
    console.error("[API ERROR] andamento da geração de pacotes", error?.response?.status);
    return null;
  }
};

/**
 * FASE 05 — Ajustes 25/08 (plano de mídias, Etapa D / decisão F05A-P3b de 26/08).
 *
 * Quantos OUTROS produtos usam este nome de arquivo. Gravar substitui a mídia de todos eles
 * — comportamento confirmado pelo PO, com a ressalva de que a tela precisa avisar antes.
 * Em produção há nomes servindo mais de mil códigos (`SCH-ESPUPOLIT.BMP` = 1.125).
 */
export type TipoMidiaN1 = 1 | 2 | 3; // 1 = Ícone, 2 = Figura, 3 = Ficha técnica

export const contarProdutosComMidia = async (
  tipoMaterial: TipoMaterialN1,
  tipoMidia: TipoMidiaN1,
  nomeArquivo: string,
  idProduto?: number
): Promise<number> => {
  try {
    if (!nomeArquivo?.trim()) return 0;
    const params = new URLSearchParams({
      tipoMaterial,
      tipoMidia: String(tipoMidia),
      nomeArquivo: nomeArquivo.trim(),
      idProduto: String(idProduto ?? 0),
    });
    const { data } = await api.get<{ total: number }>(
      "/ProdutoN1/ContarProdutosComMidia?" + params.toString()
    );
    return data?.total ?? 0;
  } catch {
    // Silencioso: falha na contagem não pode travar o salvamento — a tela degrada
    // para "sem aviso", que é o comportamento anterior.
    return 0;
  }
};

/**
 * FASE 05 — Ajustes 25/08 (plano de mídias, Etapa E — item 13 do docx de 22/08).
 * Carga em massa de mídias por .ZIP, com validação tudo-ou-nada no servidor.
 */
export const importarMidias = async (
  tipoMaterial: TipoMaterialN1,
  tipoMidia: TipoMidiaN1,
  arquivoZip: File
): Promise<{
  sucesso: boolean;
  totalArquivos: number;
  totalProdutosAfetados: number;
  arquivosSemVinculo?: string[];
  mensagem?: string;
}> => {
  try {
    const formData = new FormData();
    formData.append("arquivo", arquivoZip);
    formData.append("tipoMaterial", tipoMaterial);
    formData.append("tipoMidia", String(tipoMidia));

    const { data } = await api.post("/ProdutoN1/ImportarMidias", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    return {
      sucesso: true,
      totalArquivos: data?.totalArquivos ?? 0,
      totalProdutosAfetados: data?.totalProdutosAfetados ?? 0,
    };
  } catch (error: any) {
    // O 400 carrega a relação de arquivos órfãos — é o que a tela precisa mostrar para o
    // usuário corrigir e reenviar. Não usa getErrorsByApi para não virar toast genérico.
    const corpo = error?.response?.data;
    return {
      sucesso: false,
      totalArquivos: corpo?.totalArquivos ?? 0,
      totalProdutosAfetados: 0,
      arquivosSemVinculo: corpo?.arquivosSemVinculo ?? [],
      mensagem: corpo?.message ?? "Erro ao importar as mídias.",
    };
  }
};

// ===== Etapa D (itens 3 e 4): impacto fora do cadastro N1 =====

export interface IImpactoTipologia {
  codigoProduto: string;
  codigoTipologia: string;
  linha: string;
  projetista: string;
}

/** Decisão P2 (02/09): componente alterado que atinge a sistemista — alimenta o "Copiar lista". */
export interface IImpactoAlteracao {
  codigo: string;
  alteracao: string;
}

export interface IImpactoSistemista {
  idEmpresa: number;
  nomeFantasia: string;
  produtos?: IImpactoAlteracao[];
}

export interface IImpactoN1 {
  tipologias: IImpactoTipologia[];
  sistemistas: IImpactoSistemista[];
  temImpacto: boolean;
}

/**
 * FASE 05 Ajustes 31/08 — Etapa D: o que os produtos deste acionamento afetam fora do cadastro
 * N1. Consultado ANTES de abrir o modal de confirmação; os dois avisos apenas informam e pedem
 * confirmação, nenhum bloqueia (decisão F05B-P8).
 *
 * Falha aqui NÃO impede a transição: devolve vazio e a tela segue sem os avisos.
 */
export const verificarImpactosN1 = async (
  idEmpresa: number,
  itens: ITipoSelecionadoN1[]
): Promise<IImpactoN1> => {
  const vazio: IImpactoN1 = { tipologias: [], sistemistas: [], temImpacto: false };
  try {
    const { data } = await api.post("/ProdutoN1/VerificarImpactos", { idEmpresa, itens });
    return data ?? vazio;
  } catch {
    console.warn("Não foi possível verificar os impactos — a transição segue sem os avisos.");
    return vazio;
  }
};
